import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type Institution = Tables<"institutions">;

export interface InstitutionWithStats {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  slug: string | null;
  total_donations: number | null;
  supporters_count: number;
  is_active: boolean | null;
  cover_url: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
}

 export interface InstitutionWithExtras extends Institution {
   slug: string | null;
   cover_url: string | null;
   website: string | null;
   phone: string | null;
   cnpj: string | null;
   city: string | null;
   state: string | null;
   address: string | null;
   facebook_url: string | null;
   instagram_url: string | null;
 }

export const useInstitutions = () => {
  return useQuery({
    queryKey: ["institutions"],
    queryFn: async () => {
      console.log("[useInstitutions] Fetching institutions...");
      try {
        const { data, error } = await supabase
          .from("institutions")
          .select("*")
          .eq("is_active", true)
          .order("total_donations", { ascending: false });

        if (error) {
          console.error("[useInstitutions] Error:", error);
          throw error;
        }

        console.log("[useInstitutions] Success:", data?.length, "institutions");
        return data as Institution[];
      } catch (err) {
        console.error("[useInstitutions] Catch error:", err);
        throw err;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useInstitutionBySlug = (slug: string) => {
  return useQuery({
    queryKey: ["institution", "slug", slug],
    queryFn: async () => {
      console.log("[useInstitutionBySlug] Fetching institution:", slug);
      try {
        const { data, error } = await supabase
          .from("institutions")
          .select("*")
          .eq("slug", slug)
          .eq("is_active", true)
          .maybeSingle();

        if (error) {
          console.error("[useInstitutionBySlug] Error:", error);
          throw error;
        }

        console.log("[useInstitutionBySlug] Success:", data?.name);
        return data as InstitutionWithExtras | null;
      } catch (err) {
        console.error("[useInstitutionBySlug] Catch error:", err);
        throw err;
      }
    },
    enabled: !!slug,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useInstitutionSupporters = (institutionId: string) => {
  return useQuery({
    queryKey: ["institution", institutionId, "supporters"],
    queryFn: async () => {
      console.log("[useInstitutionSupporters] Counting supporters for:", institutionId);
      try {
        // Buscar a instituição para obter o external_id
        const { data: institution, error: instError } = await supabase
          .from("institutions")
          .select("external_id")
          .eq("id", institutionId)
          .maybeSingle();

        if (instError) {
          console.error("[useInstitutionSupporters] Error fetching institution:", instError);
          throw instError;
        }

        // Contar usuários Supabase
        const { count: supabaseCount, error: profilesError } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("institution_id", institutionId);

        if (profilesError) {
          console.error("[useInstitutionSupporters] Error counting profiles:", profilesError);
          throw profilesError;
        }

        // Contar usuários WordPress não migrados (se tiver external_id)
        let wordpressCount = 0;
        if (institution?.external_id) {
          const { count: wpCount, error: wpError } = await supabase
            .from("user_wordpress_mapping")
            .select("*", { count: "exact", head: true })
            .eq("institution_wordpress_id", institution.external_id)
            .is("supabase_user_id", null);

          if (wpError) {
            console.error("[useInstitutionSupporters] Error counting WordPress users:", wpError);
            // Não falhar, apenas logar o erro
          } else {
            wordpressCount = wpCount || 0;
          }
        }

        const total = (supabaseCount || 0) + wordpressCount;
        console.log("[useInstitutionSupporters] Count:", total, "(Supabase:", supabaseCount, "WordPress:", wordpressCount, ")");
        return total;
      } catch (err) {
        console.error("[useInstitutionSupporters] Catch error:", err);
        throw err;
      }
    },
    enabled: !!institutionId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const usePopularInstitutions = (limit: number = 4) => {
  return useQuery({
    queryKey: ["institutions", "popular", limit],
    queryFn: async () => {
      console.log("[usePopularInstitutions] Fetching popular institutions...");
      try {
        const { data, error } = await supabase
          .from("institutions")
          .select("*")
          .eq("is_active", true)
          .order("total_donations", { ascending: false })
          .limit(limit);

        if (error) {
          console.error("[usePopularInstitutions] Error:", error);
          throw error;
        }

        console.log("[usePopularInstitutions] Success:", data?.length, "institutions");
        return data as Institution[];
      } catch (err) {
        console.error("[usePopularInstitutions] Catch error:", err);
        throw err;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useInstitutionsWithStats = (limit?: number) => {
  return useQuery({
    queryKey: ["institutions", "with-stats", limit],
    queryFn: async () => {
      console.log("[useInstitutionsWithStats] Fetching institutions with stats...");
      try {
        const { data, error } = await supabase.rpc("get_institutions_with_stats", {
          p_limit: limit ?? undefined,
        });

        if (error) {
          console.error("[useInstitutionsWithStats] Error:", error);
          throw error;
        }

        console.log("[useInstitutionsWithStats] Success:", data?.length, "institutions");
        return data as InstitutionWithStats[];
      } catch (err) {
        console.error("[useInstitutionsWithStats] Catch error:", err);
        throw err;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useInstitution = (id: string) => {
  return useQuery({
    queryKey: ["institution", id],
    queryFn: async () => {
      console.log("[useInstitution] Fetching institution:", id);
      try {
        const { data, error } = await supabase
          .from("institutions")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (error) {
          console.error("[useInstitution] Error:", error);
          throw error;
        }

        console.log("[useInstitution] Success:", data?.name);
        return data as Institution | null;
      } catch (err) {
        console.error("[useInstitution] Catch error:", err);
        throw err;
      }
    },
    enabled: !!id,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};
